// Initialize Sentry via a dynamic import — that split (not the timing) is what
// keeps the ~80 KB @sentry core out of the eager bundle. Fire it immediately,
// NOT on idle: idle-gating gives no extra bundle benefit but would leave the
// boot/first-paint window — where crashes are most likely — unmonitored for
// hundreds of ms. As a dynamic import the chunk stays low-priority, so the
// browser still fetches it after the critical chunks (eager entry unchanged),
// and Sentry installs its global handlers as soon as that chunk loads.
import('./instrument').catch((error) => {
  log.error('Failed to initialize Sentry:', error);
});

import './assets/critical.css';

import('./assets/fonts.css');
import('./assets/style.css');

import { StrictMode } from 'react';
import ReactGA from 'react-ga4';

import { ApolloProvider } from '@apollo/client/react';
import { CSSReset } from '@chakra-ui/react';
import { ClerkProvider } from '@clerk/react';
import { createApolloClient, getUserToken } from '@this+that/app-config';
import { installLogRedaction } from '@this+that/utils/redactPii';
import log from 'loglevel';
import ReactDOM from 'react-dom/client';

import { App } from './App';
import { AppContext } from './context/AppContext';
import { scheduleAppShellSafetyNet } from './Layout/AppShell';
import {
  deactivateInDev,
  register as registerServiceWorker,
} from './serviceWorkerRegistration';
import { captureFirstTouchFromReferrer } from './utils/analytics/attributionCookie';
import { captureUtmFromUrl } from './utils/analytics/utmSession';
import { installStaleChunkRecoveryListener } from './utils/staleChunkRecovery';

installStaleChunkRecoveryListener();

/**
 * Runs non-critical startup work after the browser is idle so it stays off the
 * initial render/download path. Falls back to a short timeout where
 * requestIdleCallback is unavailable.
 */
const deferToIdle = (task: () => void): void => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(task);
  } else {
    setTimeout(task, 200);
  }
};

const {
  VITE_CLERK_PUBLISHABLE_KEY,
  VITE_GA_KEY,
  VITE_API_BASE_URL,
  ENV,
  PROD,
} = import.meta.env;

const client = createApolloClient(getUserToken, VITE_API_BASE_URL);

const isProductionEnvironment = ENV === 'production';
const PRODUCTION_HOSTNAME = 'assistant.thisandthat.chat';

installLogRedaction(log);
// PROD rather than isProductionEnvironment: Vite exposes only VITE_-prefixed variables to the
// client, so ENV is undefined in every deployed bundle and this ran at DEBUG in production.
// PROD is true for every `vite build`, so staging logs at WARN too.
log.setLevel(PROD ? log.levels.WARN : log.levels.DEBUG);

if (!VITE_CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}

if (isProductionEnvironment && !VITE_GA_KEY) {
  throw new Error('Missing GA Key');
}

// Initialize GA4 early but defer automatic events until user_id is set
if (isProductionEnvironment) {
  ReactGA.initialize(VITE_GA_KEY, {
    gaOptions: {
      send_page_view: false, // Prevent automatic page_view without user_id
    },
  });
}

// Record a first touch for visitors who arrived here without passing through the
// marketing site, which is the only path where nothing has written `tt_attr` yet.
// Runs before render because it reads document.referrer, and the first client-side
// route change replaces it. Writes only with analytics consent, and never overwrites
// an existing first touch. Gated on the hostname rather than ENV, which Vite never
// exposes to the client: the cookie is on the shared apex domain, where a staging
// write would pollute production data.
if (window.location.hostname === PRODUCTION_HOSTNAME) {
  captureFirstTouchFromReferrer();
}

// Also before render, and for the same reason: the first route change can drop
// the query string, and the utm on it is the only attribution a visitor who
// declined analytics consent will ever have. NOT production-gated, unlike the
// referrer capture above: that one writes a cookie on the shared apex domain,
// where a staging write would pollute production data, while this is
// session-scoped to this origin and staging cannot reach it.
captureUtmFromUrl();

// Emoji data for Slack rendering stays deferred to idle — it loads ALL
// Slack/Unicode shortcodes into cache, is only needed once Slack messages are
// viewed, and is never on the first-paint path.
deferToIdle(() => {
  import('./utils/emojiUtils')
    .then(({ initializeEmojiData }) => initializeEmojiData())
    .catch((error) => {
      log.error('Failed to initialize emoji data:', error);
    });
});

const rootElement = document.getElementById('root');

if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <ClerkProvider publishableKey={VITE_CLERK_PUBLISHABLE_KEY}>
        <ApolloProvider client={client}>
          <AppContext>
            <CSSReset />
            <App />
          </AppContext>
        </ApolloProvider>
      </ClerkProvider>
    </StrictMode>,
  );

  scheduleAppShellSafetyNet();

  if (import.meta.env.DEV) {
    deactivateInDev();
  } else {
    registerServiceWorker();
  }
}
